import { ReactNode } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useTelegram } from '../telegram'
import { isAuthenticated } from '../auth'
import { Button } from '@/shared/ui/button'
import { LogIn, AlertCircle } from 'lucide-react'

interface AuthGuardProps {
  children: ReactNode
  /** Показывать UI вместо редиректа */
  showUI?: boolean
  /** Путь для редиректа (по умолчанию /profile) */
  redirectTo?: string
}

/**
 * Компонент для защиты маршрутов
 * 
 * Проверяет авторизацию через Telegram initData.
 * Если не авторизован — показывает UI для входа или делает редирект.
 * 
 * @example
 * ```tsx
 * <AuthGuard>
 *   <ProtectedContent />
 * </AuthGuard>
 * ```
 */
export function AuthGuard({ children, showUI = true, redirectTo = '/profile' }: AuthGuardProps) {
  const { initData } = useTelegram()
  const navigate = useNavigate()
  
  const authenticated = isAuthenticated(initData)
  
  // Если авторизован — рендерим children
  if (authenticated) {
    return <>{children}</>
  }
  
  // Если не авторизован и нужен редирект
  if (!showUI) {
    const currentPath = window.location.pathname + window.location.search
    navigate({ 
      to: redirectTo, 
      search: { redirect: currentPath } 
    })
    return null
  }
  
  // Показываем UI для неавторизованных
  return (
    <div className="flex-1 flex items-center justify-center bg-paper dark:bg-dark-bg p-6">
      <div className="text-center max-w-sm">
        <div className="w-16 h-16 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center mx-auto mb-4">
          <AlertCircle className="w-8 h-8 text-amber-600 dark:text-amber-400" />
        </div>
        
        <h2 className="text-xl font-bold text-ink dark:text-white mb-2">
          Требуется авторизация
        </h2>
        
        <p className="text-gray-500 dark:text-gray-400 mb-6">
          Откройте приложение через Telegram для доступа к этой странице
        </p>
        
        <Button
          onClick={() => navigate({ to: '/' })}
          className="w-full"
        >
          <LogIn className="w-4 h-4 mr-2" />
          На главную
        </Button>
      </div>
    </div>
  )
}

/**
 * HOC для защиты страниц
 * 
 * @example
 * ```tsx
 * export const Route = createFileRoute('/checkout')({
 *   component: withAuth(CheckoutPage),
 * })
 * ```
 */
export function withAuth<P extends object>(
  Component: React.ComponentType<P>,
  options?: Omit<AuthGuardProps, 'children'>
) {
  return function AuthenticatedComponent(props: P) {
    return (
      <AuthGuard {...options}>
        <Component {...props} />
      </AuthGuard>
    )
  }
}
